import React from "react";
import { useTranslation } from "react-i18next";
import { StyledModal, StyledH2, StyledLabel } from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";

import { store } from "@components/VerticalLayout/store";
import { store as saleSupportStore, setIsLoading } from "../store";

import { capitalize } from "@helpers/general";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { showSuccessToast } from "@components/Common/SuccessToast";
import ErrorToast from "@components/Common/ErrorTost";
import { useFetchGlobal } from "../../../../api/hooks";

type ConfirmationFavoriteType = {
    isModalOpened: boolean;
    setIsModalOpened: Function;
    selectedRowData: any;
};

export function ConfirmationFavorite({
    isModalOpened,
    setIsModalOpened,
    selectedRowData,
}: ConfirmationFavoriteType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();

    const { franchiseID, userID, operatorID } = useSnapshot(store);
    const { saleSupportData, shopsSaleSupportData } = useSnapshot(
        saleSupportStore
    );
    const uuidUser = localStorage.getItem("uuidUser");

    async function favoriteSaleSupportShop(isFavorite: boolean) {
        setIsLoading(true);
        toast.dismiss();
        let dataSS: any = [];
        saleSupportData.forEach((elt: any) => {
            shopsSaleSupportData.forEach((el: any) => {
                let favoriteSaleSupportData;
                el.subRows.forEach((saleSupport: any) => {
                    if (selectedRowData.Id === saleSupport.Id) {
                        favoriteSaleSupportData = {
                            userId: userID,
                            shopId: [el.shopId],
                            franchiseId: franchiseID,
                            operatorId: operatorID,
                            uuidUser: uuidUser,
                            isShop: false,
                            data: {
                                [saleSupport.Id]: {
                                    id: saleSupport.Id,
                                    displayName: saleSupport[t("Display name")],
                                    tag: saleSupport.Tag,
                                    support_de_vente:
                                        saleSupport[t("Sale support")],
                                    shopId: saleSupport.shopId,
                                    isActive: saleSupport.isActive,
                                    isArchived: saleSupport.isArchived,
                                    isFavorite: isFavorite,
                                    advancedDisplayName:
                                        saleSupport.advancedDisplayName,
                                },
                            },
                        };
                        dataSS.push(favoriteSaleSupportData);
                    } else if (
                        elt.Id === saleSupport.Id &&
                        el.subRows.findIndex(
                            (elt: any) => elt.Id === selectedRowData.Id
                        ) !== -1
                    ) {
                        favoriteSaleSupportData = {
                            userId: userID,
                            shopId: [el.shopId],
                            franchiseId: franchiseID,
                            operatorId: operatorID,
                            uuidUser: uuidUser,
                            isShop: false,
                            data: {
                                [saleSupport.Id]: {
                                    id: saleSupport.Id,
                                    displayName: saleSupport[t("Display name")],
                                    tag: saleSupport.Tag,
                                    support_de_vente:
                                        saleSupport[t("Sale support")],
                                    shopId: saleSupport.shopId,
                                    isActive: saleSupport.isActive,
                                    isArchived: saleSupport.isArchived,
                                    isFavorite: false,
                                    advancedDisplayName:
                                        saleSupport.advancedDisplayName,
                                },
                            },
                        };
                        dataSS.push(favoriteSaleSupportData);
                    }
                });
            });
        });

        const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dataSS),
        };

        try {
            const result = await fetchGlobal<any>(
                `${process.env.REACT_APP_API_V2_URL}/settings/general/saleSupport`,
                requestOptions
            );

            if (result.error) {
                throw new Error(result.message);
            }
            showSuccessToast(
                `${
                    isFavorite
                        ? `${t(
                              "Shop favorite state has been successfully activated"
                          )}`
                        : `${t(
                              "Shop favorite state has been successfully deactivated"
                          )}`
                }`
            );
            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);
            ErrorToast(`${t("There's an error")!}`);
        }
    }

    return (
        <StyledModal toggle={true} isOpen={isModalOpened!} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setIsModalOpened(!isModalOpened);
                }}
            >
                <StyledH2>{capitalize(t("Alert"))}</StyledH2>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {t(
                        'Do you want to apply these changes for all stores? \n Attention ! If you click on "Validate", the specific settings of the shops will be modified.'
                    )}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        setIsModalOpened(!isModalOpened);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    onClick={() => {
                        favoriteSaleSupportShop(!selectedRowData.isFavorite);
                        setIsModalOpened(!isModalOpened);
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
